import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import { SpecSelectionMode, SpecValueType } from 'api/types/product-type/SpecConstraintViewModel';

/** Turkish-aware case folding for spec key/value comparisons (İ/i, I/ı, etc.). */
export function normalizeTurkishCompareKey(value: string): string {
  return value.trim().toLocaleLowerCase('tr-TR');
}

export function compareTurkishEqual(a: string, b: string): boolean {
  return normalizeTurkishCompareKey(a) === normalizeTurkishCompareKey(b);
}

/**
 * API returns SpecValueType as string enum names (JsonStringEnumConverter).
 * Normalize wire values to numeric SpecValueType for UI and query building.
 */
export function normalizeSpecValueType(valueType: unknown): SpecValueType {
  if (valueType === SpecValueType.Number || valueType === 0 || valueType === '0' || valueType === 'Number') {
    return SpecValueType.Number;
  }
  if (valueType === SpecValueType.String || valueType === 1 || valueType === '1' || valueType === 'String') {
    return SpecValueType.String;
  }
  if (valueType === SpecValueType.Enum || valueType === 2 || valueType === '2' || valueType === 'Enum') {
    return SpecValueType.Enum;
  }
  return SpecValueType.String;
}

/** Unwrap ServiceResult data: plain array or legacy paginated envelope. */
export function normalizeSpecConstraintList(data: unknown): SpecConstraintViewModel[] {
  let list: SpecConstraintViewModel[] = [];
  if (data == null) return [];
  if (Array.isArray(data)) {
    list = data.map(normalizeSpecConstraint);
  } else if (typeof data === 'object' && 'items' in data) {
    const items = (data as { items?: unknown }).items;
    if (Array.isArray(items)) {
      list = items.map(normalizeSpecConstraint);
    }
  }
  return dedupeSpecConstraints(list);
}

export function normalizeSpecConstraint(raw: SpecConstraintViewModel): SpecConstraintViewModel {
  const allowedValues = dedupeAllowedValues(raw.allowedValues ?? []);
  return {
    ...raw,
    valueType: normalizeSpecValueType(raw.valueType),
    selectionMode: normalizeSpecSelectionMode(raw.selectionMode),
    allowedValues,
  };
}

function dedupeAllowedValues(
  values: SpecConstraintViewModel['allowedValues'] | null | undefined
): SpecConstraintViewModel['allowedValues'] {
  if (!values) return [];
  const seen = new Set<string>();
  const result: NonNullable<SpecConstraintViewModel['allowedValues']> = [];
  for (const av of values) {
    const key = normalizeTurkishCompareKey(av.value);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(av);
  }
  result.sort((a, b) => a.value.localeCompare(b.value, 'tr', { sensitivity: 'base' }));
  return result;
}

function isCanonicalSpecDisplayName(displayName: string): boolean {
  return displayName === displayName.toLocaleUpperCase('tr-TR');
}

function pickPreferredSpecConstraint(
  current: SpecConstraintViewModel,
  candidate: SpecConstraintViewModel
): SpecConstraintViewModel {
  const currentCanonical = isCanonicalSpecDisplayName(current.displayName);
  const candidateCanonical = isCanonicalSpecDisplayName(candidate.displayName);
  if (candidateCanonical && !currentCanonical) return candidate;
  if (currentCanonical && !candidateCanonical) return current;
  return candidate.id > current.id ? candidate : current;
}

/** Collapse duplicate constraints that share the same normalized key (e.g. legacy vs canonical labels). */
function dedupeSpecConstraints(constraints: SpecConstraintViewModel[]): SpecConstraintViewModel[] {
  const byKey = new Map<string, SpecConstraintViewModel>();
  for (const constraint of constraints) {
    const normKey = normalizeTurkishCompareKey(constraint.key);
    const existing = byKey.get(normKey);
    byKey.set(normKey, existing ? pickPreferredSpecConstraint(existing, constraint) : constraint);
  }
  return Array.from(byKey.values());
}

export function normalizeSpecSelectionMode(mode: unknown): SpecSelectionMode {
  if (mode === SpecSelectionMode.Multi || mode === 1 || mode === '1' || mode === 'Multi') {
    return SpecSelectionMode.Multi;
  }
  return SpecSelectionMode.Single;
}
